import React from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh,
  Close as CloseIcon,
  AllInboxOutlined,
} from "@mui/icons-material";

import ConversationList from "../../../components/ConversationList";
import { invalidateConversationFilterCounts } from "../../../hooks/useConversationFilterCounts";

const ConversationListPane = ({
  isMobile,
  containerStyle,
  workspaceEmbedded = false,
  conversations,
  conversationsTimeoutMessage,
  selectedConversation,
  onSelectConversation,
  fetchNextPage,
  refetchConversations,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  initialLoading,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  advancedFilterState,
  applyChatFilterState,
  clearAdvancedFilterState,
  isLoadingDetails,
  onClose,
  onNewConversation,
  isCreatingConversation,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation("chat");
  const { t: tApp } = useTranslation("app");

  return (
    <Box
      className="tour-chat-list-pane"
      width={isMobile ? "100%" : workspaceEmbedded ? "min(32%, 360px)" : "25%"}
      minWidth={workspaceEmbedded ? 300 : undefined}
      bgcolor={theme.palette.background.default}
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{
        ...containerStyle,
        ...(workspaceEmbedded
          ? { borderRight: 1, borderColor: "divider", borderRadius: 0, boxShadow: "none" }
          : {}),
      }}
    >
      {!isMobile && (
        <Box
          className="tour-chat-list-toolbar"
          sx={{
            flexShrink: 0,
            px: 1.25,
            py: 0.75,
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: theme.palette.background.paper,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} minWidth={0} flex={1}>
            {workspaceEmbedded ? (
              <>
                <AllInboxOutlined color="primary" sx={{ fontSize: 20, flexShrink: 0 }} />
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {tApp("nav.chat")}
                </Typography>
              </>
            ) : (
              <Tooltip title={t("listPane.hideList")}>
                <IconButton size="small" onClick={onClose} aria-label={t("listPane.hideList")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.25} flexShrink={0}>
            <Tooltip title={t("listPane.refreshRequests")}>
              <IconButton
                size="small"
                onClick={() => {
                  if (typeof refetchConversations === "function") refetchConversations();
                  invalidateConversationFilterCounts(queryClient);
                }}
                aria-label={t("listPane.refreshRequests")}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isCreatingConversation ? t("listPane.creating") : t("listPane.newRequest")}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={onNewConversation}
                  disabled={isCreatingConversation}
                  aria-label={t("listPane.newRequest")}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 32,
                    height: 32,
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  {isCreatingConversation ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AddIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      )}

      <ConversationList
        conversations={conversations}
        conversationsTimeoutMessage={conversationsTimeoutMessage}
        workspaceEmbedded={workspaceEmbedded}
        selectedConversation={selectedConversation}
        onSelectConversation={onSelectConversation}
        fetchNextPage={fetchNextPage}
        refetchConversations={refetchConversations}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        initialLoading={initialLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        advancedFilterState={advancedFilterState}
        applyChatFilterState={applyChatFilterState}
        clearAdvancedFilterState={clearAdvancedFilterState}
        isLoadingDetails={isLoadingDetails}
      />
    </Box>
  );
};

export default ConversationListPane;
